import {
	BrainCircuit,
	Building,
	Clock,
	CreditCard,
	Database,
	FileText,
	FormInput,
	GitBranch,
	Globe,
	Layers,
	Mail,
	MessageSquare,
	RefreshCw,
	RotateCcw,
	ShieldCheck,
	Square,
	Database as StorageIcon,
	Table,
	UserCheck,
	Webhook,
	Workflow,
} from 'lucide-react';
import { useNodes } from 'reactflow';
import { useWorkflowContext } from '../contexts/WorkflowContext';
import { hasStartNode, isEmptyWorkflow, isTriggerNodeType } from '../lib/workflow-utils';
import type { CustomNode } from '../types/nodes';

const CATEGORIES = [
	{
		name: 'Triggers',
		color: 'emerald',
		nodes: [
			{ type: 'start.form', label: 'Form Trigger', icon: FormInput },
			{ type: 'start.webhook', label: 'Webhook Trigger', icon: Webhook },
			{ type: 'start.schedule', label: 'Schedule Trigger', icon: Clock },
			{ type: 'start.http-endpoint', label: 'HTTP Endpoint', icon: Globe },
		],
	},
	{
		name: 'Flow Control',
		color: 'emerald',
		nodes: [
			{ type: 'end', label: 'End', icon: Square },
			{ type: 'response', label: 'Response', icon: RefreshCw },
		],
	},
	{
		name: 'Processing',
		color: 'blue',
		nodes: [
			{ type: 'api-call', label: 'API Call', icon: Globe },
			{ type: 'transform', label: 'Transform', icon: Workflow },
			{ type: 'condition', label: 'Condition', icon: GitBranch },
			{ type: 'pdf', label: 'PDF Generator', icon: FileText },
		],
	},
	{
		name: 'Integrations',
		color: 'purple',
		nodes: [
			{ type: 'database', label: 'Database', icon: Database },
			{ type: 'email', label: 'Email', icon: Mail },
			{ type: 'slack', label: 'Slack', icon: MessageSquare },
			{ type: 'hubspot', label: 'HubSpot', icon: Building },
			{ type: 'google-sheets', label: 'Google Sheets', icon: Table },
			{ type: 'stripe', label: 'Stripe', icon: CreditCard },
		],
	},
	{
		name: 'Advanced',
		color: 'orange',
		nodes: [
			{ type: 'loop', label: 'Loop', icon: RefreshCw },
			{ type: 'wait', label: 'Wait/Delay', icon: Clock },
			{ type: 'webhook-listener', label: 'Webhook Listener', icon: Webhook },
			{ type: 'approval', label: 'Approval', icon: UserCheck },
			{ type: 'logger', label: 'Logger', icon: FileText },
			{ type: 'validator', label: 'Validator', icon: ShieldCheck },
			{ type: 'storage', label: 'Storage', icon: StorageIcon },
			{ type: 'webhook-caller', label: 'Webhook Caller', icon: Webhook },
			{ type: 'switch', label: 'Switch', icon: GitBranch },
			{ type: 'retry', label: 'Retry', icon: RotateCcw },
			{ type: 'parallel', label: 'Parallel', icon: Layers },
		],
	},
];

const onDragStart = (event: React.DragEvent, nodeType: string, label: string) => {
	event.dataTransfer.setData('application/reactflow', nodeType);
	event.dataTransfer.setData('application/reactflow-label', label);
	event.dataTransfer.effectAllowed = 'move';
};

export function Sidebar() {
	const nodes = useNodes() as CustomNode[];
	const { getCurrentFlowId } = useWorkflowContext();
	const hasStart = hasStartNode(nodes);
	const isEmpty = isEmptyWorkflow(nodes);

	// Check if we're editing an existing workflow
	const isEditingExistingWorkflow = !!getCurrentFlowId();

	return (
		<div className="overflow-y-auto p-4 w-64 bg-white border-r border-gray-200 transition-colors dark:bg-gray-800 dark:border-gray-700">
			<div className="mb-6">
				<h2 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
					Workflow Nodes
				</h2>
				<p className="text-sm text-gray-600 dark:text-gray-400">
					{isEditingExistingWorkflow
						? 'Drag any node to the canvas to extend your workflow'
						: isEmpty || !hasStart
						? 'Start by adding a trigger node to begin your workflow'
						: 'Drag nodes to the canvas to build your workflow'}
				</p>
			</div>

			{CATEGORIES.map(category => (
				<div key={category.name} className="mb-6">
					<h3
						className={`text-sm font-medium text-${category.color}-700 dark:text-${category.color}-400 mb-3 uppercase tracking-wide`}
					>
						{category.name}
					</h3>
					<div className="space-y-2">
						{category.nodes.map(node => {
							const isTriggerNode = isTriggerNodeType(node.type);

							// Apply Editor Behavior Rules (only for new workflows)
							let isDisabled = false;
							let disabledReason = '';

							// Skip editor behavior rules when editing existing workflows
							if (!isEditingExistingWorkflow) {
								if (isTriggerNode) {
									// Rule 2: Only allow one trigger node per canvas
									if (hasStart) {
										isDisabled = true;
										disabledReason =
											'Each workflow can only have one trigger node';
									}
								} else {
									// Rule 1: Only allow trigger nodes on empty canvas
									if (isEmpty || !hasStart) {
										isDisabled = true;
										disabledReason = 'Start with a trigger node first';
									}
								}
							}

							return (
								<div
									key={node.type}
									className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-colors ${
										isDisabled
											? `bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 cursor-not-allowed opacity-50`
											: `bg-${category.color}-50 dark:bg-${category.color}-900/20 border-${category.color}-200 dark:border-${category.color}-800 cursor-move hover:bg-${category.color}-100 dark:hover:bg-${category.color}-900/30`
									}`}
									draggable={!isDisabled}
									onDragStart={event =>
										!isDisabled && onDragStart(event, node.type, node.label)
									}
									title={isDisabled ? disabledReason : undefined}
								>
									<node.icon
										className={`w-5 h-5 ${
											isDisabled
												? 'text-gray-400 dark:text-gray-500'
												: `text-${category.color}-600 dark:text-${category.color}-400`
										}`}
									/>
									<span
										className={`text-sm font-medium ${
											isDisabled
												? 'text-gray-500 dark:text-gray-400'
												: `text-${category.color}-700 dark:text-${category.color}-300`
										}`}
									>
										{node.label}
									</span>
								</div>
							);
						})}
					</div>
				</div>
			))}

			{/* Dynamic Pro Tip based on workflow state */}
			<div className="p-4 mt-8 bg-gray-50 rounded-lg border border-gray-200 transition-colors dark:bg-gray-700/50 dark:border-gray-700">
				<div className="flex gap-2 items-center mb-2">
					<BrainCircuit className="w-5 h-5 text-blue-600 dark:text-blue-400" />
					<span className="text-sm font-medium text-gray-900 dark:text-white">
						{isEmpty || !hasStart ? 'Getting Started' : 'Pro Tip'}
					</span>
				</div>
				<p className="text-xs text-gray-600 dark:text-gray-400">
					{isEmpty || !hasStart
						? 'Every workflow needs exactly one trigger to start execution. Choose from Form, Webhook, or Schedule triggers to begin.'
						: 'Connect nodes with arrows to create your workflow. Advanced nodes provide powerful control flow and utilities.'}
				</p>
			</div>
		</div>
	);
}
